import { useState } from 'react'
import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import type { SharedMarketingProps } from '@/types/marketing'

/**
 * THE PRESS ROOM. Written for a journalist on deadline: the facts first, then
 * the stories, then text they can paste. Content and its rules live in
 * app/lib/press_content.rb — the company speaks here, never a named person.
 */
interface Story {
  id: string
  eyebrow: string
  title: string
  angle: string
  body: string
  link: { label: string; href: string }
}

interface PressPageProps extends SharedMarketingProps {
  facts: { label: string; value: string }[]
  boilerplate: { short: string; long: string }
  stories: Story[]
  coverage: { title: string; outlet: string; url: string; published_on: string | null }[]
  attribution: string
  contact: { email: string; mailto: string }
  brand_path: string
}

/** Boilerplate with a copy button. The text stays on screen and selectable, so
 *  a blocked clipboard costs nothing. */
function Boilerplate({ label, text }: { label: string; text: string }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      // Clipboard blocked — the text is right there to select.
    }
  }

  return (
    <div className="marketing-light-card h-full">
      <div className="flex items-center justify-between gap-4">
        <div className="marketing-eyebrow-mono">{label}</div>
        <button
          type="button"
          onClick={copy}
          className="rounded-full border border-black/15 px-3 py-1 text-xs font-medium text-[#151515] hover:border-black/40"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="mt-4 text-sm leading-7 text-[#3b3b3b]">{text}</p>
      <span className="sr-only" role="status">{copied ? `${label} copied` : ''}</span>
    </div>
  )
}

function isExternal(href: string) {
  return /^https?:\/\//.test(href)
}

export default function Press(props: PressPageProps) {
  return (
    <>
      <AtmosphericHero minHeight="48vh" containerClassName="hero-content">
        <div className="hero-text">
          <span className="hero-eyebrow">Press room</span>
          <h1 className="hero-headline" style={{ fontSize: 'clamp(2.5rem, 7vw, 5.5rem)' }}>
            Writing about wavbee?
          </h1>
          <p className="hero-body">
            The facts, the stories and the words to describe us — and a person to ask when
            you need more.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={props.contact.mailto} className="marketing-button-primary">Contact the press team</a>
            <a href={props.brand_path} className="marketing-button-secondary">Logos and brand assets</a>
          </div>
        </div>
      </AtmosphericHero>

      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">At a glance</div>
              <h2 className="marketing-section-title marketing-section-title-dark">The facts.</h2>
            </div>
          </ScrollReveal>

          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {props.facts.map(fact => (
              <div key={fact.label} className="marketing-light-card">
                <dt className="marketing-eyebrow-mono">{fact.label}</dt>
                <dd className="mt-2 text-lg font-semibold text-[#151515]">{fact.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="marketing-section has-glow">
        <div className="section-glow cool" style={{ width: '40rem', height: '40rem', right: '-12rem', top: '-12rem' }} />
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">Story ideas</div>
              <h2 className="marketing-section-title">What's worth writing about.</h2>
            </div>
          </ScrollReveal>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {props.stories.map((story, index) => (
              <ScrollReveal key={story.id} direction="up" delay={index * 0.06}>
                <article id={story.id} className="marketing-surface-card flex h-full flex-col">
                  <div className="marketing-eyebrow-mono">{story.eyebrow}</div>
                  <h3 className="mt-3 text-xl font-semibold text-white">{story.title}</h3>
                  <p className="mt-3 text-sm font-medium leading-7 text-white/90">{story.angle}</p>
                  <p className="mt-3 text-sm leading-7 text-white/72">{story.body}</p>
                  <a
                    href={story.link.href}
                    {...(isExternal(story.link.href) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className="mt-auto pt-5 text-sm text-[#ff6a1a] hover:underline"
                  >
                    {story.link.label} &rarr;
                  </a>
                </article>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {props.coverage.length > 0 && (
        <section className="marketing-section">
          <div className="container mx-auto px-6">
            <ScrollReveal direction="up">
              <div className="marketing-section-header">
                <div className="marketing-eyebrow-mono">In the news</div>
                <h2 className="marketing-section-title">What others have written.</h2>
              </div>
            </ScrollReveal>

            <ul className="mx-auto max-w-3xl divide-y divide-white/10">
              {props.coverage.map(item => (
                <li key={item.url} className="py-4">
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="group block">
                    <div className="marketing-eyebrow-mono">
                      {item.outlet}
                      {item.published_on && ` · ${new Date(item.published_on).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}`}
                    </div>
                    <div className="mt-1 text-lg font-semibold text-white group-hover:text-[#ff6a1a]">{item.title} &#8599;</div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">Boilerplate</div>
              <h2 className="marketing-section-title marketing-section-title-dark">About wavbee, ready to paste.</h2>
            </div>
          </ScrollReveal>

          <div className="grid gap-5 lg:grid-cols-2">
            <Boilerplate label="Short" text={props.boilerplate.short} />
            <Boilerplate label="Long" text={props.boilerplate.long} />
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-surface-card mx-auto max-w-3xl">
              <div className="marketing-eyebrow-mono">Media contact</div>
              <h2 className="mt-3 text-2xl font-semibold text-white">Questions, interviews and images</h2>
              <p className="mt-4 text-sm leading-7 text-white/72">{props.attribution}</p>
              <p className="mt-4 text-sm leading-7 text-white/72">
                Write to{' '}
                <a href={props.contact.mailto} className="text-[#ff6a1a] hover:underline">
                  {props.contact.email}
                </a>
                {' '}with “Press” in the subject. Logos and colours are on the{' '}
                <a href={props.brand_path} className="text-[#ff6a1a] hover:underline">brand page</a>.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </>
  )
}
